import { VaccinationsForm } from "app/services/api/types/residents/models/AddResidentVaccinationsForm";
import { useFieldArray, useForm } from "react-hook-form";
import { useResidentContext } from "../provider/ResidentProvider";
import { useUpdateResidentVaccinationMutation } from "app/services/api/residentsApi";
import { handleBackendError } from "ui/utils/Errors";
import { useDisplayToast } from "ui/hooks/useDisplayToast";
import { usePageFocus } from "ui/utils/FocusHook";
import { VaccinationModel } from "app/services/api/types/residents/models/VaccinationModel";

export const useHandleResidentVaccinations = () => {
  const { residentId, resident } = useResidentContext();
  const [triggerUpdateResidentVaccination] =
    useUpdateResidentVaccinationMutation();
  const { showNewToast, showErrorToast } = useDisplayToast();

  const {
    control,
    handleSubmit,
    setError,
    reset,
    formState: { isSubmitting },
  } = useForm<VaccinationsForm>({
    defaultValues: {
      vaccines: [{ id: undefined, name: "", date_of_administration: "" }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "vaccines",
  });

  usePageFocus(() => {
    if (resident?.vaccines.length) {
      reset({
        vaccines: resident.vaccines,
      });
    }
  }, [resident, reset]);

  const updateResidentVaccination = async (
    data: VaccinationsForm,
    residentId?: number,
  ) => {
    if (residentId) {
      await triggerUpdateResidentVaccination({
        residentId,
        body: data,
      }).unwrap();
    }
    return residentId;
  };

  const submitForm = () => {
    return new Promise<boolean>((resolve) => {
      if (isSubmitting) {
        resolve(false);
        return;
      }
      handleSubmit(
        async (data: VaccinationsForm) => {
          try {
            const updatedVaccines = data.vaccines
              .map((vaccine: VaccinationModel, index: number) => ({
                ...vaccine,
                id: vaccine.id,
              }))
              .filter((item) => !!item.name);

            const updatedData = { vaccines: updatedVaccines };
            await updateResidentVaccination(updatedData, residentId);

            showNewToast({
              title: "Resident Vaccination Details Updated",
              message: "Successfully updated resident's vaccination details",
              type: "success",
            });
            resolve(true);
          } catch (e) {
            const result = handleBackendError(e, setError);
            showErrorToast(e);
            resolve(result);
          }
        },
        (errors) => {
          console.error("Form errors:", errors);
          resolve(false);
        },
      )();
    });
  };

  return {
    control,
    fields,
    append: () => {
      const lastId = fields[fields.length - 1]?.id || 0;
      append({ id: lastId + 1, name: "", date_of_administration: "" });
    },
    remove,
    submitForm,
  };
};
